import { useState, useEffect, useRef } from "react";
import { drawingImages } from "../data/drawingData.js";

const ZOOM_DESKTOP = 2.5;
const ZOOM_MOBILE = 4;

function ImageMagnifier({ src, zoomed, setZoomed }) {
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const [hovered, setHovered] = useState(false);
  const imgRef = useRef(null);
  const didDrag = useRef(false);
  const lastTouch = useRef({ x: 0, y: 0 });
  const justTouched = useRef(false);
  const touchTimer = useRef(null);

  // ── Desktop ──────────────────────────────────────────
  function handleMouseEnter() {
    if (justTouched.current) return; // ignore simulated mouse events after touch
    setHovered(true);
  }

  function handleMouseLeave() {
    if (justTouched.current) return;
    setHovered(false);
  }

  function handleMouseMove(e) {
    const rect = imgRef.current.getBoundingClientRect();
    setOrigin({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  }

  // ── Mobile ───────────────────────────────────────────
  function handleTouchStart(e) {
    e.preventDefault();
    justTouched.current = true;
    clearTimeout(touchTimer.current);
    touchTimer.current = setTimeout(() => { justTouched.current = false; }, 600);
    didDrag.current = false;
    lastTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }

  function handleTouchMove(e) {
    e.preventDefault();
    if (!zoomed) return;
    const dx = e.touches[0].clientX - lastTouch.current.x;
    const dy = e.touches[0].clientY - lastTouch.current.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didDrag.current = true;
    lastTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };

    const rect = imgRef.current.getBoundingClientRect();
    const speed = 5;
    setOrigin((prev) => ({
      x: Math.min(100, Math.max(0, prev.x - (dx / (rect.width  * (ZOOM_MOBILE - 1))) * 100 * speed)),
      y: Math.min(100, Math.max(0, prev.y - (dy / (rect.height * (ZOOM_MOBILE - 1))) * 100 * speed)),
    }));
  }

  function handleTouchEnd(e) {
    e.stopPropagation(); // prevent modal's onTouchEnd from also firing
    if (didDrag.current) return;
    if (!zoomed) {
      const touch = e.changedTouches[0];
      const rect = imgRef.current.getBoundingClientRect();
      setOrigin({
        x: ((touch.clientX - rect.left) / rect.width) * 100,
        y: ((touch.clientY - rect.top) / rect.height) * 100,
      });
      setZoomed(true);
    } else {
      setZoomed(false);
      setOrigin({ x: 50, y: 50 });
    }
  }

  const scale = hovered ? ZOOM_DESKTOP : zoomed ? ZOOM_MOBILE : 1;

  return (
    <div className="overflow-hidden cursor-crosshair" style={{ touchAction: "none" }}>
      <img
        ref={imgRef}
        src={src}
        alt=""
        draggable={false}
        className="block max-w-full max-h-[40vh] md:max-h-[55vh] select-none transition-transform duration-100"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: `${origin.x}% ${origin.y}%`,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
    </div>
  );
}

export default function DrawingModal({ info, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imgZoomed, setImgZoomed] = useState(false);

  const { handle, design } = info ?? {};
  const key = handle === "none" ? "gola" : "handles";
  const images = info?.dynamic
    ? (info.productConfig?.media?.drawings ?? []).map((item) => item.url)
    : drawingImages[design?.id]?.[key] ?? [];

  function navigate(dir) {
    setCurrentIndex((i) => (i + dir + images.length) % images.length);
  }

  useEffect(() => {
    setCurrentIndex(0);
    setImgZoomed(false);
  }, [design?.id, handle]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") navigate(-1);
      if (e.key === "ArrowRight") navigate(1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, onClose]);

  if (!info || images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 md:p-6"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-4xl md:rounded-[40px] w-full max-w-[90vw] md:max-w-5xl p-6 md:p-10 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={() => { if (imgZoomed) setImgZoomed(false); }}
      >
        {/* Left arrow */}
        {images.length > 1 && (
          <button
            onClick={() => navigate(-1)}
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 text-6xl md:text-7xl text-gray-400 hover:text-gray-700 transition-colors select-none leading-none"
          >
            &#8249;
          </button>
        )}

        {/* Image with magnifier */}
        <div className="flex items-center justify-center min-h-[40vh] md:min-h-[55vh] px-8 md:px-12">
          <ImageMagnifier
            key={images[currentIndex]}
            src={images[currentIndex]}
            zoomed={imgZoomed}
            setZoomed={setImgZoomed}
          />
        </div>

        {/* Dots */}
        {images.length > 1 && (
          <div className="flex justify-center items-center gap-2 min-h-5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === currentIndex
                    ? "bg-gray-800"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>
        )}

        {/* Right arrow */}
        {images.length > 1 && (
          <button
            onClick={() => navigate(1)}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 text-6xl md:text-7xl text-gray-400 hover:text-gray-700 transition-colors select-none leading-none"
          >
            &#8250;
          </button>
        )}
      </div>
    </div>
  );
}
